/**
 * Shared shapes between the panel's backend and frontend, plus mirrors of the
 * wire contracts of the two supervised systems:
 *   - MatchSignal   — the monitor's consolidated event (365scores/SIGNALS.md)
 *   - BotEvent/...  — the bot's --json-events stdio protocol (stdio-bridge.ts)
 * Mirrored (not imported) so the panel builds standalone on the VPS even when
 * the sibling repos move; the shapes are wire contracts, versioned by hand.
 */

// ── monitor: consolidated signals ───────────────────────────────────────────
export type MatchSignal = {
  id: string;
  match: string;
  type: string;
  team: "home" | "away" | null;
  minute: number | null;
  addedTime: number | null;
  player: string | null;
  score: string | null;
  homeTeam: string | null;
  awayTeam: string | null;
  note: string | null;
  ts: string;
  source: string;
  feed: string;
  /** deep link to the match page on `source` — optional: absent on signals from
   *  an older monitor build (the row falls back to plain text). */
  url?: string | null;
  /** stable per-feed identity "site/slug" — backs the grouping view. Optional:
   *  absent on signals from an older monitor build. */
  feedSlug?: string;
  /** 1/2 = regulation halves, 3/4 = extra time, 5 = penalty shootout. Optional:
   *  only feeds that carry a period stamp it (see 365scores/lib/s365.ts). */
  period?: number | null;
  /** GOAL_CANCELED only, and only when the monitor derived it from a score
   *  retraction: false = the vanished goal had been seen by ONE source (a ghost
   *  goal — a feed error, not a VAR call), true = another source had it too.
   *  null = the cancellation arrived as an explicit incident, so the question
   *  doesn't apply. Kept apart in the stats for exactly that reason. */
  corroborated?: boolean | null;
  /** `SCORE` only: this baseline is a RESYNC the operator asked for, carrying
   *  what a Betfair score feed counts right now — not the ordinary mid-join
   *  seed. It is the one signal allowed to take a board DOWN, so the feed shows
   *  it as a correction rather than as another baseline. */
  resync?: boolean;
};

/** Bet-type category a trading rule belongs to — mirrors polymarket-soccer-bot's
 *  `src/strategy/rules/types.ts` (duplicated across the repo boundary like
 *  `BridgeCommand`; the two are not code-coupled, only wire-compatible). Drives
 *  the watchlist's per-match "Tipi di scommessa" checkboxes. */
/**
 * Categorie di scommessa che il bot conosce. Specchio di `BetCategory` in
 * `polymarket-soccer-bot/src/strategy/rules/types.ts`: se una manca qui, il suo
 * interruttore per partita non compare nel pannello e l'operatore non può
 * spegnerla.
 */
export type BetCategory = "cancellation" | "penalty" | "over-under" | "btts" | "winner" | "first-to-score" | "exact-score" | "fair-value";

export const BET_CATEGORIES: readonly BetCategory[] = ["cancellation", "penalty", "over-under", "btts", "winner", "first-to-score", "exact-score", "fair-value"];

/**
 * Il payload di un segnale `FAIR_VALUE`. Specchio di `FairValuePayload` nel bot
 * (`src/types/signal.ts`) e della specifica in `365scores/SIGNALS.md`.
 *
 * `platforms` è opzionale perché il campo è arrivato dopo: conta le AZIENDE
 * dietro la misura, mentre `engines` conta i listini. Nove marchi di una
 * piattaforma sola sono un parere ripetuto nove volte, e la loro dispersione
 * tende a zero — per questo la pagina mostra entrambi i numeri.
 */
export type FairValuePayload = {
  kind: string;
  line?: string;
  outcome: string;
  fairProb: number;
  buyMaxCents: number;
  engines: number;
  platforms?: number;
  sigmaPoints: number;
  capturedAt: string;
  live: boolean;
  home?: string;
  away?: string;
};

/** Una riga della tabella `fair_values`, come la legge la pagina. */
export type FairValueRow = {
  match: string;
  kind: string;
  line: string;
  outcome: string;
  fairProb: number;
  buyMaxCents: number;
  engines: number;
  platforms: number | null;
  sigmaPoints: number;
  capturedAt: string;
  live: number;
  home: string | null;
  away: string | null;
};

// ── goal / cancellation statistics ──────────────────────────────────────────
/** Signal types that count as a goal — the denominator of the cancellation
 *  rate. Mirrors the monitor's SCORE_IDENTIFIED set (365scores/consolidator.ts):
 *  those are exactly the events that move the scoreboard. */
export const GOAL_TYPES: readonly string[] = ["GOAL", "OWN_GOAL", "PENALTY_GOAL"];

/** Everything mirrored into `goal_events`: the goals plus the cancellations. */
export const GOAL_EVENT_TYPES: ReadonlySet<string> = new Set([...GOAL_TYPES, "GOAL_CANCELED"]);

/** Bucket used for the "when do cancellations happen" breakdown. `key` is what
 *  the UI prints; `max` is inclusive and null on the last one (90'+ swallows
 *  stoppage time and anything past it). */
export type MinuteBucket = { key: string; min: number; max: number | null };

/** Counters for one grouping (a league, a minute bucket, or the whole period).
 *  `canceled` and `canceledGhost` are DISJOINT and never summed into one number:
 *  a ghost cancellation is a feed that invented a goal and took it back, not a
 *  referee chalking one off. */
export type GoalCounts = {
  goals: number;
  canceled: number;
  canceledGhost: number;
  /** canceled / goals as a percentage, or null when there were no goals at all */
  pct: number | null;
};

export type LeagueGoalStats = GoalCounts & { league: string; matches: number };

export type MinuteGoalStats = GoalCounts & { bucket: string };

export type GoalStats = {
  from: string;
  to: string;
  total: GoalCounts & { matches: number };
  byLeague: LeagueGoalStats[];
  byMinute: MinuteGoalStats[];
  /** matches seen in the period whose league couldn't be resolved yet */
  unresolvedLeagues: number;
};

/** One chalked-off goal, for the per-league drill-down. */
export type CanceledGoalRow = {
  id: string;
  ts: string;
  match: string;
  /** watch_groups.label when the group still exists, else the raw match key */
  label: string;
  league: string;
  team: "home" | "away" | null;
  minute: number | null;
  score: string | null;
  source: string;
  feed: string;
  note: string | null;
  corroborated: boolean | null;
};

/** An operator-declared match group in the watch-list (set of per-site links that
 *  ARE the same fixture), with its Polymarket binding. Authored in the panel UI. */
export type WatchGroup = {
  id: string;
  label: string | null;
  links: string[];
  /** bound Polymarket event slug, or null when not resolved yet */
  polymarket: string | null;
  title: string | null;
  status: "resolved" | "needs-link";
  /** false = the operator switched this match off — not watched, like '#' in matches.txt */
  enabled: boolean;
  /** bet categories the operator switched OFF for this match; [] = every category active */
  disabledCategories: BetCategory[];
  /** live-video page the panel auto-opens on a goal/VAR/penalty; null = never open */
  videoUrl: string | null;
  /** when the operator added the match. The one order of this list that never
   *  moves — `updatedAt` is rewritten by every automatic market pre-check, so
   *  the Segnali lanes seed their order from this instead (see lane-marks.ts). */
  createdAt: string;
  updatedAt: string;
};

// ── bot: --json-events stdout lines ─────────────────────────────────────────
/** Sotto questa soglia di quote una posizione e' piatta, non piccola. Il bot
 *  tiene in snapshot anche le righe chiuse (il loro PnL realizzato conta ancora
 *  nel totale), quindi chi elenca l'esposizione APERTA deve filtrarle. */
export const POSITION_EPS = 1e-9;

export type Position = {
  tokenId: string;
  match?: string;
  size: number;
  avgPrice: number;
  realizedPnl: number;
};

/** A resting order open on the book — the panel lists these so an operator can
 *  cancel a specific one (mirrors the bot's OpenOrder wire shape). */
export type OpenOrder = {
  orderId: string;
  tokenId: string;
  side: "BUY" | "SELL";
  price: number;
  size: number;
  match?: string;
};

export type BridgeSnapshot = {
  kind: "snapshot";
  ts: string;
  mode: "PAPER" | "LIVE";
  killed: boolean;
  /** May be absent from an older bot build — treat as false. */
  autoSellBlocked?: boolean;
  positions: Position[];
  usdc: number;
  openOrders: number;
  /** Details of the resting orders counted by `openOrders` (may be absent from
   *  an older bot build — treat as []). */
  openOrdersList?: OpenOrder[];
};

/**
 * The strategy's own believed score/period for one match right now (mirrors
 * the bot's "match-state" BotEvent, itself backed by Strategy.currentScore).
 * Purely observational — backs a dashboard readout so an operator can catch
 * the bot's internal tally silently desyncing from the true live score (e.g. a
 * goal signal missing `team` never gets counted — see MatchState.applyGoal;
 * confirmed live on eto-fc-kf-vikingur, 2026-07-14).
 */
export type MatchStateEvent = {
  match: string;
  home: number;
  away: number;
  period: number;
  periodHome: number;
  periodAway: number;
};

/**
 * One completed round-trip on a single token (opened from flat, back to flat).
 * The bot emits this the moment a position flattens (kind "position-closed"),
 * so it is restart-safe and authoritative — see polymarket-soccer-bot's
 * Bot.trackRound. The panel persists it and shows it in the Closed-positions card.
 */
export type ClosedPosition = {
  tokenId: string;
  match: string | null;
  tag: string | null;
  mode: "PAPER" | "LIVE";
  shares: number;
  avgEntry: number;
  avgExit: number;
  realizedPnl: number;
  openedAt: string;
  closedAt: string;
  openReason: string | null;
  closeReason: string | null;
};

/**
 * One condensed *decision* the bot took on a position, projected from the raw
 * bot_events stream (intent/fill/rejected/position-closed) for the "Decisioni"
 * card. Ladder rungs that fire in a burst (same match/strategy/side) collapse
 * into a single row (`rungs` = how many raw events were folded together).
 *   - "try"      — strategy decided to attempt a position (intent)
 *   - "open"     — a BUY actually executed on the book (fill)
 *   - "close"    — a round-trip flattened (position-closed) or a SELL fill
 *   - "rejected" — the risk manager blocked an attempt (rejected)
 */
export type Decision = {
  ts: string;
  category: "try" | "open" | "close" | "rejected";
  match: string | null;
  side: "BUY" | "SELL" | null;
  strategy: string | null; // rule tag (e.g. "base-gol-over-ladder")
  reason: string | null; // intent.reason / closeReason / rejection reason
  price: number | null; // size-weighted avg price (try/open)
  avgEntry: number | null; // close only
  avgExit: number | null; // close only
  size: number | null; // shares (open/close); null for try/rejected
  rungs: number; // raw events folded into this row (1 = single)
  pnl: number | null; // close only
  tokenId: string | null;
};

/** Any other {kind: ...} line from the bot (signal/intent/order/fill/...). */
export type BotEvent = { kind: string; [key: string]: unknown };

export type BridgeCommand =
  | { cmd: "set-kill"; on: boolean }
  | { cmd: "cancel-all" }
  | { cmd: "cancel"; orderId: string }
  /** Pull every resting order on one match — the undo behind a mis-tapped
   *  manual signal (see /api/manual-signal/undo). */
  | { cmd: "cancel-match"; match: string }
  | { cmd: "sell-position"; tokenId: string; size?: number }
  | { cmd: "resolve-market"; match: string; slug: string }
  // `id` is the watch-group the entry came from; the bot echoes it back on the
  // PrecheckRow so the result can be bound to that group. The panel has always
  // sent it (Watchlist.precheckEntries) — it was just missing from this type,
  // which is why the send sites needed a cast.
  | { cmd: "precheck"; entries: Array<{ id?: string; label: string; slug: string }> }
  | { cmd: "set-match-categories"; match: string; disabled: BetCategory[] }
  | { cmd: "set-auto-sell-block"; on: boolean }
  /** Compra a mano una casella della lavagna (pagina Quote). `kind`/`line` sono
   *  nel vocabolario del MOTORE QUOTE (WINNER, OU+"2.5", BTTS, FTS): il bot
   *  possiede il registry e fa lui la traduzione ai propri mercati. */
  | {
      cmd: "buy";
      match: string;
      kind: string;
      line?: string;
      outcome: string;
      priceCents: number;
      sizeUsd: number;
      note?: string;
      /** Correlazione per attendere QUESTO esito: l'ack del bot lo riporta
       *  identico. Serve solo qui — ogni altro comando è "manda e dimentica". */
      id?: string;
    };

/** One watch-list match's pre-check outcome (mirrors the bot's PrecheckRow).
 *  `id` is the originating watch-group when the entry came from one — it's the
 *  live match key, so a manual resolve for that row must target it. */
export type PrecheckRow = { id?: string; label: string; key: string; ok: boolean; title: string | null; winner: boolean; ouLines: number; btts: boolean };

/**
 * Where a watch-group is in its hunt for a Polymarket market, as far as THIS
 * panel process knows. Deliberately not persisted: a search dies with the bot
 * that was running it, and a "searching" row restored from disk after a restart
 * would be a lie that never resolves.
 *
 * `watch_groups.status` alone cannot say this. It is written 'needs-link' at
 * insert, so it reads "not found" from the moment a match is added — before the
 * bot has been asked, and forever if the bot is down.
 *
 *   searching  a precheck or resolve-market command is in flight for it
 *   not-found  the bot answered, and had no market for it
 *   timed-out  the bot never answered (it died, or the command was lost)
 *   unsearched nothing has been asked about it since this bot spawned
 */
export type MarketSearchPhase = "searching" | "not-found" | "timed-out" | "unsearched";
export type MarketSearchState = { match: string; phase: MarketSearchPhase; since: string };

/**
 * A match the bot couldn't auto-resolve to a Polymarket market (status
 * "unresolved" → the manual-override card alarms), or one resolved since (auto,
 * or via a pasted slug). `slug` is the saved override that the panel re-applies
 * after a bot restart; `markets`/`title` describe what it resolved to.
 */
export type MarketResolution = {
  match: string;
  homeTeam: string | null;
  awayTeam: string | null;
  status: "unresolved" | "resolved";
  slug: string | null;
  title: string | null;
  markets: string[];
  by: string | null;
  firstSeen: string;
  updatedAt: string;
};

/** A secondary copy-trade wallet, REDACTED view (mirrors src/wallets.ts — the
 *  private key lives only in BOT_DIR/wallets.json and never crosses the API). */
export type WalletView = {
  id: string;
  label: string;
  funder: string;
  eoa: string;
  signatureType: number;
  scale: number;
  enabled: boolean;
  createdAt: string;
};

// ── supervisor ──────────────────────────────────────────────────────────────
/** `fairodds` è **facoltativa**: viene registrata solo se `FAIR_ODDS_DIR` è
 *  impostata, così un impianto che non usa il motore quote si comporta
 *  esattamente come prima (nessuna unità, nessun log, nessun riavvio). */
export type UnitName = "monitor" | "bot" | "fairodds";

export type UnitStatus = {
  name: UnitName;
  desired: "running" | "stopped";
  running: boolean;
  pid: number | null;
  startedAt: string | null;
  restarts: number;
  lastExitCode: number | null;
};

// ── panel ⇄ browser messages (WS /ws) ───────────────────────────────────────
export type LogLine = { ts: string; proc: UnitName; stream: "stdout" | "stderr"; line: string };

export type FullState = {
  /** The panel-chosen trading mode (what the bot is/will be spawned with). */
  mode: "paper" | "live";
  units: UnitStatus[];
  snapshot: BridgeSnapshot | null;
  /** Latest believed score/period per match (see MatchStateEvent), keyed by match. */
  matchScores: Record<string, MatchStateEvent>;
  signalsTap: { connected: boolean; detail: string };
  counts: { signals: number; orders: number; fills: number; rejected: number };
  recentSignals: MatchSignal[];
  /** Pre-folded (see fold-events.ts): repeated (kind,text) runs collapsed with `count`. */
  recentEvents: Array<{ ts: string; kind: string; text: string; count: number }>;
  recentLogs: LogLine[];
  marketResolutions: MarketResolution[];
  watchGroups: WatchGroup[];
  /** Per-group search phase (see MarketSearchState). Absent on an older backend
   *  — treat as [], i.e. fall back to the plain resolved/needs-link reading. */
  marketSearch?: MarketSearchState[];
  /** Secondary copy-trade wallets (absent on an older backend — treat as []). */
  wallets?: WalletView[];
  /** Quali famiglie di mercato servono a ogni categoria, lette dalle regole del
   *  bot (vedi market-needs.ts). Assente su un backend vecchio: senza, il badge
   *  torna al verde di prima invece di inventare avvisi. */
  categoryNeeds?: Partial<Record<BetCategory, string[]>>;
};

export type UiMessage =
  | { type: "hello"; state: FullState }
  | { type: "signal"; signal: MatchSignal }
  | { type: "bot-event"; ts: string; kind: string; text: string }
  | { type: "snapshot"; snapshot: BridgeSnapshot }
  | { type: "match-state"; state: MatchStateEvent }
  | { type: "closed-position"; row: ClosedPosition }
  | { type: "proc-status"; units: UnitStatus[] }
  | { type: "signals-tap"; connected: boolean; detail: string }
  /** Stato del tap sui fair value. Il flusso dei valori NON viaggia qui: sono
   *  centinaia di messaggi per giro, e la pagina li chiede con /api/fair-values. */
  | { type: "fair-tap"; connected: boolean; detail: string }
  | { type: "mode-changed"; mode: "paper" | "live" }
  | { type: "log-line"; line: LogLine }
  | { type: "market-resolution"; resolution: MarketResolution }
  | { type: "market-resolution-removed"; match: string }
  | { type: "resolve-error"; match: string; error: string }
  | { type: "precheck"; ts: string; rows: PrecheckRow[] }
  | { type: "market-search"; states: MarketSearchState[] }
  | { type: "watchlist"; groups: WatchGroup[] }
  | { type: "wallets"; wallets: WalletView[] };
